import React, { useState, useEffect, useMemo } from 'react';

import AssignTableExpenses from './AssignTableExpenses';
import AssignTableVenues from './AssignTableVenues';

import Button from 'CommonComponents/Button';
import CheckboxInput from 'CommonComponents/CheckboxInput';
import TableFilterModal from 'CommonComponents/TableFilterModal';

import PlusCircleIcon from 'Assets/icons/plus-circle.svg';

const LOCAL_STORAGE_FILTERS_KEY = window.expense_id ? `TEMPLATE_ASSIGN_FILTERS_${window.expense_id}` : null;

export default function AssignTable(props) {
    const [showExpense, setShowExpense] = useState([]);
    const [showUncommitted, setShowUncommitted] = useState(true);
    const [showCommitted, setShowCommitted] = useState(true);
    const [showTotal, setShowTotal] = useState(true);
    const [filters, setFilters] = useState(() => {
        const initialFilters = LOCAL_STORAGE_FILTERS_KEY && JSON.parse(window.localStorage.getItem(LOCAL_STORAGE_FILTERS_KEY));
        return initialFilters?.venues ? { venues: { ...initialFilters.venues } } : { venues: {} };
    });

    const selectedVenuesIds = useMemo(
        () =>
            Object.entries(filters.venues).reduce((ids, [venueId, selected]) => {
                selected && ids.push(venueId);
                return ids;
            }, []),
        [filters],
    );

    const selectedVenues = useMemo(() => props.venues.filter(({ id }) => selectedVenuesIds.includes(String(id))), [filters, props.venues]);

    useEffect(() => {
        if (!filters?.venues) {
            return;
        }

        const existingVenues = props.venues.reduce((existingVenues, venue) => {
            existingVenues[venue.id] = true;
            return existingVenues;
        }, {});

        setFilters({ venues: { ...existingVenues, ...filters.venues } });
    }, []);

    useEffect(() => window.localStorage.setItem(LOCAL_STORAGE_FILTERS_KEY, JSON.stringify(filters)), [filters]);

    useEffect(() => {
        const showed = [...showExpense];
        if (!showed.length) {
            props.expenses.forEach((expense) => {
                showed.push(true);
            });

            setShowExpense(showed);
        }
    }, [props.expenses]);

    useEffect(() => {
        if (!Object.keys(filters.venues).length && props.venues?.length) {
            selectAll();
        }
    }, [props.venues]);

    const selectAll = (selected = true) => {
        setFilters((state) => ({
            ...state,
            venues: props.venues.reduce((value, venue) => {
                value[venue.id] = selected;
                return value;
            }, {}),
        }));
    };

    const toggleSelect = (id) => {
        setFilters((state) => ({
            ...state,
            venues: { ...state.venues, [id]: !state.venues[id] },
        }));
    };

    const toggleColumnsDisplay = (columnType) => {
        if (columnType === 'committed') {
            setShowCommitted(!showCommitted);
        } else if (columnType === 'uncommitted') {
            setShowUncommitted(!showUncommitted);
        } else {
            setShowTotal(!showTotal);
        }
    };

    const toggleVisibility = (expense, show = false) => {
        if (expense.type === 'template_divider') {
            let startIndex = -1;
            let endIndex = -1;

            for (let i = 0; i < props.expenses.length; i++) {
                const exp = props.expenses[i];
                if (startIndex === -1) {
                    startIndex = exp.id === expense.id ? i : startIndex;
                } else if (endIndex === -1) {
                    if (exp.type === 'template_divider' && !exp.deleted) {
                        endIndex = i;
                        break;
                    }
                }
            }

            endIndex = endIndex === -1 ? props.expenses.length : endIndex;
            const showed = [...showExpense];

            const initVal = showExpense[startIndex + 1];
            const val = show || !initVal;

            for (let i = 0; i < showed.length; i++) {
                if (i > startIndex && i < endIndex) {
                    showed[i] = val;
                }
            }

            setShowExpense(showed);
        }
    };

    return (
        <div>
            <div className="tw-flex tw-justify-between tw-mb-6">
                <div>
                    {props.editableTable && props.templateAssignImportUrl && props.canBeEdited && (
                        <Button small variant="primary" as="a" href={props.templateAssignImportUrl}>
                            <PlusCircleIcon className="tw-w-3.5 tw-h-3.5 tw-mr-2" />
                            {$t('Upload Template')}
                        </Button>
                    )}
                </div>
                <div className="tw-flex tw-gap-4">
                    <div className="tw-flex tw-justify-between tw-gap-2">
                        <CheckboxInput id="committed" checked={showCommitted} label="Committed" onChange={() => toggleColumnsDisplay('committed')} />
                        <CheckboxInput id="uncommitted" checked={showUncommitted} label="Uncommitted" onChange={() => toggleColumnsDisplay('uncommitted')} />
                        <CheckboxInput id="total" checked={showTotal} label="Total" onChange={() => toggleColumnsDisplay('total')} />
                    </div>
                    <TableFilterModal>
                        <div className="tw-space-y-4 tw-min-w-96">
                            <div>
                                <div className="tw-flex tw-justify-between tw-items-center tw-mb-2">
                                    <div className="tw-text-xs tw-text-charcoal">{$t('Venues')}</div>
                                    <div className="tw-space-x-2">
                                        <button className="tw-text-xs tw-font-semibold tw-underline" onClick={() => selectAll()}>
                                            {$t('Select all')}
                                        </button>
                                        <button className="tw-text-xs tw-font-semibold tw-underline" onClick={() => selectAll(false)}>
                                            {$t('Unselect all')}
                                        </button>
                                    </div>
                                </div>
                                <div className="tw-grid tw-grid-cols-3 tw-gap-2">
                                    {props.venues.map((venue) => {
                                        return (
                                            <div key={venue.id}>
                                                <CheckboxInput
                                                    id={venue.id}
                                                    checked={filters.venues[venue.id] || false}
                                                    label={venue.name}
                                                    onChange={() => toggleSelect(venue.id)}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </TableFilterModal>
                </div>
            </div>

            {'venues' === props.tableHeaders ? (
                <AssignTableVenues
                    showExpense={showExpense}
                    expenses={props.expenses}
                    venues={selectedVenues}
                    totals={props.totals}
                    editableTable={props.editableTable}
                    parentName={props.parentName}
                    toggleVisibility={toggleVisibility}
                    handleRotate={props.handleRotate}
                    showCommitmentModal={props.showCommitmentModal}
                    handleQuantityChange={props.handleQuantityChange}
                    showCommitted={showCommitted}
                    showUncommitted={showUncommitted}
                    showTotal={showTotal}
                    selectable={props.selectable}
                    toggleFinalState={props.toggleFinalState}
                    finalStates={props.finalStates}
                    forProcurement={props.forProcurement}
                    quantityLinkStates={props.quantityLinkStates}
                    deferredQuantityLinks={props.deferredQuantityLinks}
                    openDeferredQuantityPicker={props.openDeferredQuantityPicker}
                    clearDeferredQuantityLink={props.clearDeferredQuantityLink}
                />
            ) : (
                <AssignTableExpenses
                    showExpense={showExpense}
                    expenses={props.expenses}
                    venues={selectedVenues}
                    totals={props.totals}
                    editableTable={props.editableTable}
                    parentName={props.parentName}
                    toggleVisibility={toggleVisibility}
                    handleRotate={props.handleRotate}
                    showCommitmentModal={props.showCommitmentModal}
                    handleQuantityChange={props.handleQuantityChange}
                    showCommitted={showCommitted}
                    showUncommitted={showUncommitted}
                    showTotal={showTotal}
                    selectable={props.selectable}
                    toggleFinalState={props.toggleFinalState}
                    finalStates={props.finalStates}
                    forProcurement={props.forProcurement}
                    quantityLinkStates={props.quantityLinkStates}
                />
            )}
        </div>
    );
}
